import { Button, ButtonProps, CircularProgress } from "@mui/material";
import React from "react";
import { styled } from "@mui/material/styles";

const StyledButton = styled(Button)<{ isEmailValid?: boolean }>`
  &.primary-button-m {
    position: relative;
    border-radius: 8px;
    background-color: ${props => props.isEmailValid ? '#3660F4' : '#f2f6fa'};
    width: 100%;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    box-sizing: border-box;
    gap: 6px;
    text-align: left;
    font-size: 14px;
    color: ${props => props.isEmailValid ? '#ffffff' : '#6a7896'};
    font-family: Inter;
  }

  .focus-border {
    width: calc(100% + 8px);
    position: absolute;
    margin: 0 !important;
    height: calc(100% + 8px);
    top: -4px;
    right: -4px;
    bottom: -4px;
    left: -4px;
    border-radius: 10px;
    border: 2px solid #3660f4;
    box-sizing: border-box;
    overflow: hidden;
    opacity: 0;
    z-index: 0;
  }

  .primary-button {
    position: relative;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    z-index: 1;
  }
`;

/**
 * Renders a button component with optional loading state.
 *
 * @component
 * @param {ButtonProps & { loading?: boolean; isEmailValid?: boolean }} props - The button props and loading state.
 * @param {boolean} props.loading - Determines if the button is in a loading state.
 * @param {boolean} props.disabled - Determines if the button is disabled.
 * @param {boolean} props.isEmailValid - Determines if the email is valid (changes button color).
 * @param {ReactNode} props.children - The content of the button.
 * @returns {JSX.Element} The rendered button component.
 */
export default function LoadingButton({
  loading,
  disabled,
  children,
  className,
  isEmailValid,
  ...props
}: ButtonProps & {
  loading?: boolean;
  isEmailValid?: boolean;
}) {
  return (
    <StyledButton 
      disabled={disabled || loading} 
      className={`primary-button-m ${className || ''}`}
      isEmailValid={isEmailValid}
      {...props}
    >
      <span className="primary-button">
        {loading ? (
          <CircularProgress 
            size={24} 
            sx={{ 
              color: isEmailValid ? '#ffffff' : 'inherit' 
            }} 
          />
        ) : children}
      </span>
    </StyledButton>
  );
}
